import React from "react"
import { ProductParentSearchHit, ProductParentSearchHits } from "lib/types/generated/graphql-types"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"

import {
  Container,
  StyledTableHeader,
  HeaderName,
  StatusContainer,
  ProductImageContainer,
  ProductImage,
  Product,
  Name,
  Id,
  EmptyList
} from "./Shared.styled"

import TableRow from "components/Ui/Table/TableRow"
import TableBlurLoading from "../Ui/Table/TableBlurLoading"
import ErrorMessage from "../Ui/Messages/ErrorMessage"
import Status from "components/Ui/Status"

import { ReactComponent as ProductIcon } from "images/icons/bags-shopping.svg"
import placeholder from "images/placeholder.svg"
import { handleImageError } from "helpers/image"

type Props = {
  productData: ProductParentSearchHits | undefined
  loading: boolean
  searchQuery: string
  error: object | undefined
}

export const ProductTable = ({ productData, loading, searchQuery, error }: Props) => {
  const navigate = useNavigate()

  const tableHeader = (
    <StyledTableHeader>
      <HeaderName>Name</HeaderName>
      <StatusContainer className="hideOnSmallScreens">Status</StatusContainer>
      <div className="hideOnSmallScreens">Created</div>
      <div className="hideOnSmallScreens">Updated</div>
    </StyledTableHeader>
  )

  const getRows = (product?: ProductParentSearchHit) =>
    product ? (
      <TableRow
        key={product.id}
        handleOnClick={() => {
          navigate(`/products/${product.id}`)
        }}
      >
        <ProductImageContainer>
          <ProductImage src={product?.imageUrl as string} onError={handleImageError} />
        </ProductImageContainer>
        <Product>
          <Name>{product.name}</Name>
          <Id>{product.id}</Id>
        </Product>
        <StatusContainer className="hideOnSmallScreens">
          {product.isActive ? <Status status="Active" /> : <Status status="Inactive" />}
        </StatusContainer>
        <div className="hideOnSmallScreens">
          {dayjs(product.created).format("YYYY-MM-DD, HH:MM")}
        </div>
        <div className="hideOnSmallScreens">
          {dayjs(product.updated).format("YYYY-MM-DD, HH:MM")}
        </div>
      </TableRow>
    ) : (
      <>
        <ProductImageContainer>
          <ProductImage src={placeholder} />
        </ProductImageContainer>
        <Product>
          <Name>Product name</Name>
          <Id>Product id</Id>
        </Product>
        <StatusContainer>
          <Status status="Default" />
        </StatusContainer>
        <div className="hideOnSmallScreens">
          <p>{dayjs("2000-00-00").format("YYYY-MM-DD")}</p>
        </div>
        <div className="hideOnSmallScreens">
          <p>{dayjs("2000-00-00").format("YYYY-MM-DD")}</p>
        </div>
      </>
    )

  if (loading && !searchQuery)
    return <TableBlurLoading numberOfRows={4} tableHeader={tableHeader} rows={getRows()} />

  return (
    <Container>
      {error && (
        <ErrorMessage
          showRefreshButton
          message={
            <>
              Error loading products. Try refreshing the page, or contact{" "}
              <a href="mailto:support@brinkcommerce.com">support</a>.
            </>
          }
        />
      )}
      {productData && productData?.hits?.length > 0 ? (
        <>
          {tableHeader}
          <div>{productData.hits.map((product) => getRows(product))}</div>
        </>
      ) : (
        <EmptyList>
          <ProductIcon />
          <p> No products found</p>
        </EmptyList>
      )}
    </Container>
  )
}
